import { normalizeDashboards } from '../../../../utils'
import { ensureUniqueObjectIds } from '@edge/spark/utils'
import { getActiveDashboardId, getTempLayouts, setIsEditingLayouts } from '../../../../Stores'
import {
    ClientUserDashboardsDocument as userDashboardsQuery,
    type ClientUserDashboardsQuery,
    type MutationResolvers,
    UpdateUserDashboardsDocument as mutation,
} from '../../../../generated/graphql'
import { createDialog } from '@edge/spark/feature-dialog'
import type { ApolloResolverContext } from '@edge/spark/io-apollo'
import { getUpdatedDashboardCards } from '../../../../utils/getUpdatedDashboardCards'
import { getActiveAuctionId } from '@edge/spark/io-roles'

export const saveLayoutsResolver: MutationResolvers<ApolloResolverContext>['saveLayouts'] = async (
    _root,
    { dashId, userId },
    { cache, client },
) => {
    console.debug('io-dashboards saveLayoutsResolver deprecated, refactor (soon)')

    if (!client) {
        throw new Error('Missing ApolloClient')
    }

    const auctionId = getActiveAuctionId()

    if (!auctionId) {
        throw new Error('saveLayouts: Missing AuctionId')
    }

    if (userId && !dashId) {
        throw new Error('Custom userId without dashId')
    }

    const tempLayouts = getTempLayouts()

    if (!tempLayouts) {
        throw new Error('No tempLayouts')
    }

    const { data } = await client.query<ClientUserDashboardsQuery>({
        query: userDashboardsQuery,
        variables: { userId },
        fetchPolicy: 'network-only',
    })

    const activeDashboardId = getActiveDashboardId()
    const dashboardIdToUpdate = dashId || activeDashboardId
    const savedDashboards = normalizeDashboards(data?.dashboards)
    const dashboardToUpdateIndex = savedDashboards.findIndex((d) => d.id === dashboardIdToUpdate)
    const dashboardToUpdate = savedDashboards[dashboardToUpdateIndex]

    if (!dashboardToUpdate) {
        createDialog({
            action: 'OK',
            message: `Error: Dashboard with id:${dashboardIdToUpdate} not found`,
            timeout: 5000,
        })

        return null
    }

    const parsedTempLayouts = JSON.parse(tempLayouts)
    const updatedCards = getUpdatedDashboardCards(parsedTempLayouts, dashboardToUpdate.cards)
    const updatedDashboard = {
        ...dashboardToUpdate,
        cards: updatedCards,
    }
    const updatedDashboards = [
        ...savedDashboards.slice(0, dashboardToUpdateIndex),
        updatedDashboard,
        ...savedDashboards.slice(dashboardToUpdateIndex + 1),
    ]
    const cleanedDashboards = ensureUniqueObjectIds(updatedDashboards)
    const stringifiedUpdatedDashboards = cleanedDashboards.map((d) => JSON.stringify(d))
    const admin = userId ? { auctionId, subjectUserId: userId } : undefined

    await client.mutate({
        mutation,
        variables: { admin, dashboards: stringifiedUpdatedDashboards },
        optimisticResponse: {
            updateUser: {
                __typename: 'UserMutationResponse',
                authenticationId: null,
                statusCode: 200,
                statusMessage: null,
            },
        },
        update: (store): void => {
            setIsEditingLayouts(false)
            if (userId) {
                store.writeData({
                    data: {
                        [`dashboards({"userId":"${userId}"})`]: stringifiedUpdatedDashboards,
                        administrator: {
                            __typename: 'Administrator',
                            [`user({"input":{"admin":{"auctionId":"${auctionId}","subjectUserId":"${userId}"}}})`]: {
                                __typename: 'User',
                                dashboards: stringifiedUpdatedDashboards,
                            },
                        },
                    },
                })
            } else {
                store.writeData({
                    data: {
                        'dashboards({})': stringifiedUpdatedDashboards,
                        user: {
                            __typename: 'User',
                            dashboards: stringifiedUpdatedDashboards,
                        },
                    },
                })
            }
        },
    })

    return true
}
